// src/utils/subscription.js
// Utilities for managing user subscriptions

import { supabase } from '../supabaseClient';

// Plan details for display
export const PLAN_DETAILS = {
  free: {
    name: 'Free',
    searches: '100/month',
    alerts: '0',
    features: ['Basic AI recommendations', 'Price predictions', 'Jet lag optimizer'],
    color: '#6b7280',
  },
  basic: {
    name: 'Basic',
    searches: '250/month',
    alerts: '10/month',
    features: ['Basic AI recommendations', 'Price predictions', 'Jet lag optimizer', 'Email price alerts'],
    color: '#2563eb',
  },
  pro: {
    name: 'Pro',
    searches: 'Unlimited',
    alerts: '25/month',
    features: ['Advanced AI recommendations', 'Price predictions', 'Jet lag optimizer', 'Email price alerts', 'Priority support'],
    color: '#8b5cf6',
  },
  max: {
    name: 'Max',
    searches: 'Unlimited',
    alerts: '100/month',
    features: ['Premium AI recommendations', 'Price predictions', 'Jet lag optimizer', 'Email price alerts', 'Priority support', 'Ad-free experience'],
    color: '#f59e0b',
  },
};

/**
 * Get user's current subscription from Supabase
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Subscription data
 */
export const getUserSubscription = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      // If no subscription found, return free plan
      if (error.code === 'PGRST116') {
        return {
          plan: 'free',
          status: 'active',
          current_period_end: null,
          cancel_at_period_end: false,
        };
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return {
      plan: 'free',
      status: 'active',
      current_period_end: null,
      cancel_at_period_end: false,
    };
  }
};

/**
 * Get user's current month usage
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Usage data
 */
export const getUserUsage = async (userId) => {
  const currentMonth = new Date().toISOString().slice(0, 7); // "2025-01"
  
  try {
    const { data, error } = await supabase
      .from('user_usage')
      .select('*')
      .eq('user_id', userId)
      .eq('month', currentMonth)
      .single();

    if (error) {
      return {
        search_count: 0,
        alert_count: 0,
      };
    }

    return data;
  } catch (error) {
    console.error('Error fetching usage:', error);
    return {
      search_count: 0,
      alert_count: 0,
    };
  }
};

/**
 * Open Stripe Customer Portal for subscription management
 * @param {string} userId - User ID
 * @returns {Promise<string|null>} Portal URL or null on error
 */
export const openCustomerPortal = async (userId) => {
  try {
    const response = await fetch('/api/create-portal-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: userId,
        returnUrl: window.location.href,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to open portal');
    }

    return data.url;
  } catch (error) {
    console.error('Error opening customer portal:', error);
    return null;
  }
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};
